import React from 'react'
import { Box, IconButton, Typography } from '@mui/material'
import CircleRoundedIcon from '@mui/icons-material/CircleRounded'
import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded'
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded'
import TodayRoundedIcon from '@mui/icons-material/TodayRounded'
import ViewAgendaRoundedIcon from '@mui/icons-material/ViewAgendaRounded'
import ZoomInRoundedIcon from '@mui/icons-material/ZoomInRounded'
import ZoomOutRoundedIcon from '@mui/icons-material/ZoomOutRounded'

export const PlannerTopBar = ({
  summaryCards,
  legendItems,
  visibleHours,
  setVisibleHours,
  diaPlanificador,
  setDiaPlanificador,
  addDaysToDateValue,
  toDateInputValue,
  minVisibleHours,
  maxVisibleHours,
  zoomStepHours,
  vistaCompacta,
  setVistaCompacta,
}) => (
  <Box
    sx={{
      px: { xs: 1.5, md: 2 },
      py: 0.6,
      borderBottom: '1px solid #E6ECF5',
      backgroundColor: '#fffffff5',
      display: 'flex',
      flexWrap: 'wrap',
      justifyContent: 'space-between',
      alignItems: 'center',
      gap: 1,
    }}
  >
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: '1fr',
          sm: 'repeat(3, minmax(0, 1fr))',
          xl: 'repeat(5, minmax(0, 1fr))',
        },
        gap: 0.8,
        flex: { xs: '1 1 100%', lg: '1 1 auto' },
        minWidth: { lg: 420 },
      }}
    >
      {summaryCards.map((card) => {
        const Icon = card.icon

        return (
          <Box
            key={card.label}
            sx={{
              px: 0.9,
              py: 0.55,
              borderRadius: '8px',
              border: '1px solid #E5EAF3',
              backgroundColor: '#FFFFFF',
              display: 'flex',
              alignItems: 'center',
              gap: 0.8,
              minHeight: 42,
            }}
          >
            <Box
              sx={{
                width: 28,
                height: 28,
                borderRadius: '8px',
                display: 'grid',
                placeItems: 'center',
                backgroundColor: `${card.color}16`,
                color: card.color,
                flexShrink: 0,
              }}
            >
              <Icon sx={{ fontSize: 18 }} />
            </Box>

            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontSize: '0.62rem', color: '#64748B', textTransform: 'uppercase', fontWeight: 700, lineHeight: 1 }}>
                {card.label}
              </Typography>
              <Typography sx={{ fontSize: '0.98rem', fontWeight: 600, color: card.color, lineHeight: 1.05 }}>
                {card.value}
              </Typography>
            </Box>
          </Box>
        )
      })}
    </Box>

    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 0.75,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <IconButton
          size='small'
          onClick={() => setDiaPlanificador((dia) => addDaysToDateValue(dia, -1))}
          sx={{
            border: '1px solid #DEE6F2',
            backgroundColor: '#FFFFFF',
          }}
        >
          <ChevronLeftRoundedIcon fontSize='small' />
        </IconButton>

        <Box
          component="input"
          type="date"
          value={diaPlanificador}
          onChange={(event) => setDiaPlanificador(event.target.value || toDateInputValue())}
          sx={{
            height: 34,
            px: 1,
            borderRadius: '8px',
            border: '1px solid #DEE6F2',
            backgroundColor: '#FFFFFF',
            color: '#334155',
            fontFamily: 'inherit',
            fontWeight: 800,
            fontSize: '0.82rem',
            outline: 'none',
            boxSizing: 'border-box',
            '&:focus': {
              borderColor: '#F57C00',
              boxShadow: '0 0 0 3px rgba(245, 124, 0, 0.14)',
            },
          }}
        />

        <IconButton
          size='small'
          onClick={() => setDiaPlanificador((dia) => addDaysToDateValue(dia, 1))}
          sx={{
            border: '1px solid #DEE6F2',
            backgroundColor: '#FFFFFF',
          }}
        >
          <ChevronRightRoundedIcon fontSize='small' />
        </IconButton>

        <IconButton
          size='small'
          onClick={() => setDiaPlanificador(toDateInputValue())}
          sx={{
            border: '1px solid #DEE6F2',
            backgroundColor: '#FFFFFF',
            color: diaPlanificador === toDateInputValue() ? '#F57C00' : '#64748B',
          }}
        >
          <TodayRoundedIcon fontSize='small' />
        </IconButton>
      </Box>

      <Box sx={{ display: 'flex', gap: 0.75 }}>
        <IconButton
          size='small'
          onClick={() => setVistaCompacta((compacta) => !compacta)}
          title={vistaCompacta ? 'Vista ampliada' : 'Vista compacta'}
          sx={{
            border: '1px solid #DEE6F2',
            backgroundColor: vistaCompacta ? '#FFF4E6' : '#FFFFFF',
            color: vistaCompacta ? '#F57C00' : '#64748B',
          }}
        >
          <ViewAgendaRoundedIcon fontSize='small' />
        </IconButton>

        <IconButton
          size='small'
          onClick={() => setVisibleHours((hours) => Math.max(hours - zoomStepHours, minVisibleHours))}
          sx={{
            border: '1px solid #DEE6F2',
            backgroundColor: '#FFFFFF',
          }}
        >
          <ZoomOutRoundedIcon fontSize='small' />
        </IconButton>

        <IconButton
          size='small'
          onClick={() => setVisibleHours((hours) => Math.min(hours + zoomStepHours, maxVisibleHours))}
          sx={{
            border: '1px solid #DEE6F2',
            backgroundColor: '#FFFFFF',
          }}
        >
          <ZoomInRoundedIcon fontSize='small' />
        </IconButton>
      </Box>

      <Box
        sx={{
          px: 1.75,
          py: 0.9,
          borderRadius: '8px',
          border: '1px solid #DEE6F2',
          backgroundColor: '#FFFFFF',
          color: '#334155',
          fontWeight: 800,
        }}
      >
        {visibleHours}h
      </Box>
    </Box>

    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 2,
      }}
    >
      {legendItems.map((item) => (
        <Box
          key={item.label}
          sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}
        >
          <CircleRoundedIcon sx={{ color: item.color, fontSize: 14 }} />
          <Typography
            sx={{
              color: '#64748B',
              fontWeight: 700,
              fontSize: '0.82rem',
              textTransform: 'uppercase',
            }}
          >
            {item.label}
          </Typography>
        </Box>
      ))}
    </Box>
  </Box>
)
